// src/components/TopicView.jsx

import React, { useState, useEffect, useRef } from 'react';
import { 
    BookOpen, ChevronDown, Play, 
    HelpCircle, FileText, Video, Link as LinkIcon, 
    X, ExternalLink, Activity, Briefcase, MessageSquare, Sparkles,
    ChevronLeft, ChevronRight, CheckCircle2, RefreshCw, Compass, Check, ClipboardList
} from 'lucide-react';
import { db, auth } from '../config/firebase-config';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { initialTopicContent } from '../data/content';
import { useOrganization } from '../context/OrganizationContext';
import { activePathNodeFor, markPathNodeDone, clearActivePathNode, PREVIEW_UID, takePendingWorksheet } from '../utils/guidedPath';
import { pinnedFirst, pinnedModulesFirst, isManagedTopic } from '../utils/topicMerge';
import { getWorksheet, journalModuleFor } from '../data/worksheets';
import { WorksheetOverlay } from './worksheets/WorksheetRunner';
import PledgeBlock from './custom/PledgeBlock';
import { divisionOf } from '../utils/teams';
import ConcussionBlock from './custom/ConcussionBlock';
import { recordContentEvent } from '../utils/contentEvents';

// --- VERTICAL CONTENT PACKS ---
// Content is assembled in four tiers: hardcoded shared base -> global /topics
// overlay -> the org's VERTICAL PACK (/topics_sport or /topics_corporate) ->
// the org's /private_topics. Packs let sport-specific libraries grow without
// polluting the corporate side (and vice versa). Any module or resource may
// also carry `verticals: ['sport'|'corporate']` — when present and the org's
// industry isn't listed, it's dropped at merge time (cheap pruning of base
// content later, no rebuild).
const packCollectionFor = (industry) =>
    industry === 'corporate' ? 'topics_corporate' : (industry === 'sport' ? 'topics_sport' : null);

const allowedForVertical = (item, industry) =>
    !industry || !Array.isArray(item?.verticals) || item.verticals.includes(industry);

// --- HELPER: USER CONTEXT ---
const getUserOrgId = async (uid) => {
    if (!uid) return null;
    try {
        const snap = await getDoc(doc(db, "users", uid));
        return snap.exists() ? snap.data().organizationId : null;
    } catch (e) {
        console.error("Org Fetch Error", e);
        return null;
    }
};

// --- ADVANCED YOUTUBE PLAYER ---
const CustomYouTubePlayer = ({ videoId, onNext, onEnded }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);
    // Latest onEnded without re-creating the player (the YT event closure is
    // built once per videoId).
    const onEndedRef = useRef(onEnded);
    useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);

            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    showinfo: 0
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            
                            // --- NEW: FORCE EXIT FULLSCREEN ON END ---
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) { /* Safari */
                                document.webkitExitFullscreen().catch(() => {});
                            } else if (document.mozFullScreenElement) { /* Firefox */
                                document.mozCancelFullScreen().catch(() => {});
                            } else if (document.msFullscreenElement) { /* IE11 */
                                document.msExitFullscreen().catch(() => {});
                            }

                            setIsEnded(true);
                            if (onEndedRef.current) onEndedRef.current();
                        } else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            {/* YouTube Iframe mounts here */}
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            
            {/* The ERI HUB Custom End Screen Overlay */}
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                        <CheckCircle2 size={40} className="text-eas-blue" />
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl font-black mb-8 text-center px-4 tracking-tight">Module Complete</h3>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button 
                            onClick={() => {
                                setIsEnded(false);
                                if (playerRef.current) playerRef.current.playVideo();
                            }} 
                            className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border border-white/10"
                        >
                            <RefreshCw size={18} /> Replay
                        </button>
                        
                        {onNext && (
                            <button 
                                onClick={onNext}
                                className="px-8 py-3.5 bg-eas-blue hover:bg-blue-600 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                            >
                                Next Video <ChevronRight size={20} />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


const TopicView = ({ topicId, onAskAi, currentUser }) => {
    const [topicData, setTopicData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeModule, setActiveModule] = useState(null);
    const [activeTool, setActiveTool] = useState(null);
    const [userOrgId, setUserOrgId] = useState(null);

    // "Why you're here": the Guided Path node that sent the user to this topic
    // (sessionStorage hand-off from GuidedPath / AssessmentComplete). Finishing
    // a video here — or tapping Mark done — completes that step.
    // Guided Path "Open checklist": land straight in the sheet once content is loaded
    const [pendingWorksheet, setPendingWorksheet] = useState(() => takePendingWorksheet());
    useEffect(() => {
        if (!pendingWorksheet || loading || !topicData) return;
        const res = (topicData.modules || []).flatMap(m => m.resources || []).find(r => r.worksheetId === pendingWorksheet);
        if (res) openTool(res);
        setPendingWorksheet(null);
    // openTool is recreated each render; the effect keys off the pending id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingWorksheet, loading, topicData]);

    const [pathNode, setPathNode] = useState(() => activePathNodeFor(topicId));
    const [pathDone, setPathDone] = useState(false);
    useEffect(() => { setPathNode(activePathNodeFor(topicId)); setPathDone(false); }, [topicId]);

    const completePathStep = async () => {
        if (!pathNode || pathDone) return;
        // In super-admin preview the dashboard is a synthetic user — progress must
        // go to the preview store, not the real signed-in account.
        const uid = isPreviewMode ? PREVIEW_UID : auth.currentUser?.uid;
        if (!uid) return;
        try {
            await markPathNodeDone(uid, pathNode.id);
            setPathDone(true);
        } catch (e) {
            console.warn('Path step save failed', e);
        }
    };
    const backToPath = () => {
        clearActivePathNode();
        window.location.hash = 'dashboard';
    };

    // Vertical from the live (or previewed) org — decides which pack merges in.
    // Legacy orgs without an industry are treated as sport, matching the org
    // editor; a community hub reads the base library only (no pack, and content
    // tagged for a vertical is dropped), as the editor's industry note says.
    const { orgData, isPreviewMode } = useOrganization();
    const industry = orgData ? (orgData.industry === 'corporate' ? 'corporate' : (orgData.industry === 'community' ? 'community' : 'sport')) : null;
    const packCollection = packCollectionFor(industry);
    // Private content belongs to the org on screen: the previewed org when a
    // super-admin is previewing, else the member's own. Reading the member's org
    // here showed a super-admin THEIR org's pages while previewing a client's, so
    // a client's private content looked empty.
    const effectiveOrgId = orgData?.id || userOrgId;

    // Content-use events (utils/contentEvents.js): the page, and each thing opened
    // on it. Count-only, no user id.
    useEffect(() => {
        recordContentEvent('topic_view', { user: currentUser, org: orgData, isPreviewMode, topicId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topicId]);
    const openTool = (res) => {
        setActiveTool(res);
        if (!res) return;
        recordContentEvent(res.type === 'Worksheet' && res.worksheetId ? 'worksheet_open' : 'resource_open',
            { user: currentUser, org: orgData, isPreviewMode, topicId, resourceId: res.worksheetId || res.id, resourceType: res.type });
    };

    // 1. Resolve User Organization First
    useEffect(() => {
        const fetchOrg = async () => {
            const user = auth.currentUser;
            if (user) {
                const orgId = await getUserOrgId(user.uid);
                setUserOrgId(orgId);
            }
        };
        fetchOrg();
    }, []);

    // 2. Dual-Listener Data Fetch
    useEffect(() => {
        setLoading(true);
        setActiveModule(null); 

        const hardcoded = initialTopicContent[topicId] || { title: 'Loading...', modules: [] };
        let unsubGlobal = () => {};
        let unsubPack = () => {};
        let unsubPrivate = () => {};

        let globalData = { modules: [] };
        let packData = { modules: [] };
        let privateData = { modules: [] };

        // Union a tier's modules into the map: same id -> resources merged
        // (deduped by id), new id -> module added.
        const unionInto = (moduleMap, modules) => {
            (modules || []).forEach(m => {
                if (moduleMap.has(m.id)) {
                    const existing = moduleMap.get(m.id);
                    const mergedResources = [...(existing.resources || []), ...(m.resources || [])];
                    const uniqueResources = Array.from(new Map(mergedResources.map(r => [r.id, r])).values());
                    moduleMap.set(m.id, { ...existing, ...m, resources: uniqueResources });
                } else {
                    moduleMap.set(m.id, m);
                }
            });
        };

        const mergeAndSet = () => {
            const baseModules = [...(hardcoded.modules || [])];
            const moduleMap = new Map();

            if (isManagedTopic(globalData)) {
                // Managed by the CMS: the saved list IS the shared content —
                // module order and deletions are honoured, base is not merged.
                globalData.modules.forEach(m => moduleMap.set(m.id, m));
            } else {
                baseModules.forEach(m => moduleMap.set(m.id, m));

                if (globalData.modules) {
                    globalData.modules.forEach(m => {
                        moduleMap.set(m.id, m);
                    });
                }
            }

            // Tier 3: the org's vertical pack (sport / corporate) — additive.
            unionInto(moduleMap, packData.modules);
            // Tier 4: the org's private content — additive.
            unionInto(moduleMap, privateData.modules);

            // Vertical pruning: drop any module/resource tagged for a different
            // industry. Untagged content (all of today's base) shows everywhere.
            const modules = pinnedModulesFirst(Array.from(moduleMap.values()))
                .filter(m => allowedForVertical(m, industry))
                .map(m => ({
                    ...m,
                    // "Place at: top" additions from packs / private content float first
                    resources: pinnedFirst((m.resources || []).filter(r => allowedForVertical(r, industry)))
                }));

            // Interactive checklists / worksheets for this topic (code registry) —
            // appended as their own module unless content already references them.
            const journalModule = journalModuleFor(topicId);
            if (journalModule) {
                const referenced = new Set(modules.flatMap(m => (m.resources || []).map(r => r.worksheetId).filter(Boolean)));
                const missing = journalModule.resources.filter(r => !referenced.has(r.worksheetId));
                if (missing.length > 0 && !modules.some(m => m.id === journalModule.id)) {
                    modules.push({ ...journalModule, resources: missing });
                }
            }

            const finalData = {
                ...hardcoded,
                ...globalData,
                ...packData,
                ...privateData,
                modules
            };

            if (finalData.modules.length > 0 || finalData.description) {
                setTopicData(finalData);
                setActiveModule(curr => curr || (finalData.modules[0]?.id || null));
            } else {
                setTopicData(null);
            }
            setLoading(false);
        };

        const globalRef = doc(db, "topics", topicId);
        unsubGlobal = onSnapshot(globalRef, (snap) => {
            if (snap.exists()) globalData = snap.data();
            mergeAndSet();
        });

        if (packCollection) {
            unsubPack = onSnapshot(doc(db, packCollection, topicId), (snap) => {
                packData = snap.exists() ? snap.data() : { modules: [] };
                mergeAndSet();
            }, (error) => {
                console.warn("Vertical pack unavailable", error);
                mergeAndSet();
            });
        }

        if (effectiveOrgId) {
            const privateRef = doc(db, "organizations", effectiveOrgId, "private_topics", topicId);
            unsubPrivate = onSnapshot(privateRef, (snap) => {
                if (snap.exists()) privateData = snap.data();
                mergeAndSet();
            }, (error) => {
                console.warn("Private Content Access Denied or Missing", error);
                mergeAndSet(); 
            });
        } else {
            mergeAndSet();
        }

        return () => {
            unsubGlobal();
            unsubPack();
            unsubPrivate();
        };
    }, [topicId, effectiveOrgId, packCollection, industry]);

    // --- HELPER FUNCTIONS ---
    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const getPdfEmbedUrl = (url) => {
        if (!url) return '';
        if (url.includes('drive.google.com') && url.includes('/view')) {
            return url.replace('/view', '/preview');
        }
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    };

    const getIconForType = (type) => {
        if (type === 'Video') return <Video size={20}/>;
        if (type === 'Link') return <LinkIcon size={20}/>;
        if (type === 'PDF') return <FileText size={20}/>;
        if (type === 'Template') return <Briefcase size={20}/>;
        if (type === 'Worksheet') return <ClipboardList size={20}/>;
        return <Activity size={20}/>;
    };

    // --- NAVIGATION LOGIC ---
    let currentModuleResources = [];
    if (topicData && activeModule) {
        const mod = topicData.modules.find(m => m.id === activeModule);
        if (mod && mod.resources) currentModuleResources = mod.resources;
    }
    
    const currentResourceIndex = activeTool ? currentModuleResources.findIndex(r => r.id === activeTool.id) : -1;
    const hasNext = currentResourceIndex !== -1 && currentResourceIndex < currentModuleResources.length - 1;
    const hasPrev = currentResourceIndex > 0;

    const handleNextResource = () => {
        if (hasNext) setActiveTool(currentModuleResources[currentResourceIndex + 1]);
    };

    const handlePrevResource = () => {
        if (hasPrev) setActiveTool(currentModuleResources[currentResourceIndex - 1]);
    };

    // --- RENDER STATES ---
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-96 space-y-4 animate-pulse">
                <div className="w-16 h-16 bg-slate-200 rounded-full"></div>
                <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            </div>
        );
    }

    if (!topicData) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4"><BookOpen size={32} className="text-slate-400" /></div>
                <h2 className="text-2xl font-bold text-slate-700 mb-2">Topic Not Found</h2>
                <p className="text-slate-500 max-w-md">We couldn't find content for "<span className="font-mono text-sm bg-slate-100 px-1 py-0.5 rounded">{topicId}</span>".</p>
            </div>
        );
    }

    const isInteractiveWorksheet = (tool) => !!(tool && tool.type === 'Worksheet' && tool.worksheetId && getWorksheet(tool.worksheetId));

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
            {/* INTERACTIVE WORKSHEET / CHECKLIST — full screen, step-through, journaled per user */}
            {activeTool && isInteractiveWorksheet(activeTool) && (
                <WorksheetOverlay
                    worksheetId={activeTool.worksheetId}
                    currentUser={currentUser}
                    topicId={topicId}
                    onPathStepDone={() => setPathDone(true)}
                    onClose={() => setActiveTool(null)}
                />
            )}
            {activeTool && !isInteractiveWorksheet(activeTool) && (
                <div className="fixed inset-0 z-[200] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 md:p-4">
                    <div className="bg-white w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl flex flex-col h-[90vh] md:h-[85vh]">
                        
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10 shrink-0">
                            <h3 className="font-bold text-sm md:text-lg text-slate-800 flex items-center gap-3 truncate pr-2">
                                <div className="p-2 bg-blue-100 text-eas-blue rounded-lg shrink-0">{getIconForType(activeTool.type)}</div>
                                <span className="truncate">{activeTool.title}</span>
                            </h3>
                            <button onClick={() => setActiveTool(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors shrink-0"><X size={20} className="text-slate-500"/></button>
                        </div>
                        
                        <div className="flex-1 bg-slate-100 relative overflow-hidden flex flex-col">
                            
                            {/* --- RESPONSIVE VIDEO WRAPPER --- */}
                            {activeTool.type === 'Video' && (
                                <div className="w-full h-full bg-black flex items-center justify-center p-2 md:p-6 overflow-hidden">
                                    {/* Forces 16:9 Aspect Ratio but restricts Max Height so controls never clip */}
                                    <div 
                                        className="w-full relative" 
                                        style={{ aspectRatio: '16/9', maxHeight: '100%', maxWidth: '1024px' }}
                                    >
                                        <CustomYouTubePlayer 
                                            videoId={getYouTubeVideoId(activeTool.url)} 
                                            onNext={hasNext ? handleNextResource : null} 
                                            onEnded={completePathStep}
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {(activeTool.type === 'PDF' || activeTool.type === 'Link') && (
                                <div className="w-full h-full relative group">
                                    <iframe src={getPdfEmbedUrl(activeTool.url)} className="w-full h-full" frameBorder="0" title="Resource Viewer"></iframe>
                                    <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <a href={activeTool.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg shadow-xl flex items-center gap-2 hover:bg-black transition-colors"><ExternalLink size={14}/> Open in New Tab</a>
                                    </div>
                                </div>
                            )}
                            {(activeTool.type === 'Template' || activeTool.type === 'Worksheet') && (
                                <div className="overflow-y-auto h-full p-4 md:p-8">
                                    <pre className="whitespace-pre-wrap font-sans text-slate-600 text-sm md:text-base leading-relaxed p-4 md:p-8 bg-white rounded-xl border border-slate-200 shadow-sm max-w-3xl mx-auto">{activeTool.content || "No content available."}</pre>
                                </div>
                            )}
                        </div>

                        {/* --- MODAL NAVIGATION FOOTER --- */}
                        <div className="p-3 md:p-4 border-t border-slate-100 bg-white flex justify-between items-center shrink-0 z-10">
                            <div className="flex gap-2">
                                {hasPrev ? (
                                    <button onClick={handlePrevResource} className="px-3 md:px-4 py-2 bg-slate-50 text-slate-600 rounded-lg font-bold hover:bg-slate-100 border border-slate-200 transition-colors text-xs md:text-sm flex items-center gap-1 md:gap-2">
                                        <ChevronLeft size={16} /> <span className="hidden sm:inline">Previous</span>
                                    </button>
                                ) : <div />}
                            </div>
                            
                            <div className="flex gap-2">
                                {hasNext && (
                                    <button onClick={handleNextResource} className="px-4 md:px-5 py-2 bg-eas-blue text-white rounded-lg font-bold hover:bg-blue-600 transition-colors flex items-center gap-1 md:gap-2 text-xs md:text-sm shadow-md">
                                        Next <span className="hidden sm:inline">Resource</span> <ChevronRight size={16}/>
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* GUIDED PATH CONTEXT — only when a path node sent the user here */}
            {pathNode && (
                <div className={`rounded-2xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in fade-in ${pathDone ? 'bg-emerald-50 border-emerald-200' : 'bg-brand-light border-brand-primary/30'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white ${pathDone ? 'bg-emerald-500' : 'bg-brand-primary'}`}>
                            {pathDone ? <CheckCircle2 size={18} /> : <Compass size={18} />}
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{pathDone ? 'Step complete' : 'On your guided path'}</p>
                            <p className="text-sm font-bold text-slate-800 truncate">{pathNode.title}</p>
                            {!pathDone && <p className="text-xs text-slate-500 font-medium">Watch a video here and this step is marked done.</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {!pathDone && (
                            <button onClick={completePathStep} className="px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-bold hover:border-brand-primary/40 hover:text-brand-primary transition-colors flex items-center gap-1.5">
                                <Check size={13} strokeWidth={3} /> Mark done
                            </button>
                        )}
                        <button onClick={backToPath} className={`px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${pathDone ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-brand-primary text-white hover:bg-brand-dark'}`}>
                            <ChevronLeft size={13} /> Back to my path
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="px-3 py-1 bg-blue-50 text-eas-blue text-xs font-bold uppercase tracking-wider rounded-full">Learning Path</span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">{topicData.title}</h1>
                        <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">{topicData.description || "Master this topic through our guided modules and resources."}</p>
                    </div>
                    
                    <button 
                        onClick={() => onAskAi && onAskAi(`Help me understand ${topicData.title}`)}
                        className="flex items-center justify-center w-full md:w-auto gap-3 px-5 py-2.5 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 text-white rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-cyan-500/40 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 font-bold tracking-wide border border-white/20 group relative overflow-hidden shrink-0"
                    >
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-700 ease-in-out"></div>
                        <MessageSquare size={24} className="fill-white/10 shrink-0" />
                        <span className="text-sm whitespace-nowrap">Ask AI Coach</span>
                        <Sparkles size={20} className="text-yellow-400 animate-pulse shrink-0" />
                    </button>
                </div>
            </div>

            {/* A pledge / code / charter authored on the org's private topic doc
                (its `landing` map) sits above the modules - e.g. Hockey Alberta's
                Player Pledge at the top of Respect & Belonging. */}
            <PledgeBlock
                landing={topicData.landing}
                // The member's team facts for a form's prefill: the team name, its division
                // (read off the name), and the association as the roster import recorded it
                // (organizations/{id}.teamMeta). Never the member's name or email.
                context={{
                    team: currentUser?.groupId || '',
                    division: currentUser?.groupId ? divisionOf(currentUser.groupId) : '',
                    association: orgData?.teamMeta?.[currentUser?.groupId]?.association || ''
                }}
                onNavigate={(id) => { window.location.hash = id; }}
                onAction={(a) => recordContentEvent('pledge_link', { user: currentUser, org: orgData, isPreviewMode, topicId, resourceId: a.label, resourceType: 'Link' })}
            />

            {/* A designed page an org's private topic doc asks for by name (`block`):
                today the Concussions page (data/concussions.js), above the org's
                documents, which stay ordinary modules below it. */}
            {topicData.block === 'concussions' && (
                <ConcussionBlock currentUser={currentUser} orgData={orgData} onAction={(a) => recordContentEvent('resource_open', { user: currentUser, org: orgData, isPreviewMode, topicId, resourceId: a.label, resourceType: 'Link' })} />
            )}

            <div className="space-y-4">
                {(!topicData.modules || topicData.modules.length === 0) ? (
                    <div className="p-12 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50"><p className="text-slate-400 font-medium">No content has been added to this topic yet.</p></div>
                ) : (
                    topicData.modules.map((module, index) => {
                        const isOpen = activeModule === module.id;
                        return (
                            <div key={module.id} className={`bg-white border transition-all duration-300 rounded-2xl overflow-hidden ${isOpen ? 'border-eas-blue shadow-md ring-1 ring-blue-100' : 'border-slate-200 hover:border-blue-300'}`}>
                                <button onClick={() => setActiveModule(isOpen ? null : module.id)} className="w-full flex items-center justify-between p-6 text-left">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isOpen ? 'bg-eas-blue text-white' : 'bg-slate-100 text-slate-500'}`}>{index + 1}</div>
                                        <div><h3 className={`text-lg font-bold ${isOpen ? 'text-eas-blue' : 'text-slate-800'}`}>{module.title}</h3><p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{module.resources?.length || 0} Resources</p></div>
                                    </div>
                                    <ChevronDown size={20} className={`text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-eas-blue' : ''}`} />
                                </button>
                                {isOpen && (
                                    <div className="border-t border-slate-100 bg-slate-50/50 p-4 sm:p-6 space-y-3 animate-in slide-in-from-top-2">
                                        {(!module.resources || module.resources.length === 0) && (<p className="text-sm text-slate-400 italic pl-2">This module is empty.</p>)}
                                        {module.resources?.map((res) => (
                                            <div key={res.id} className="group flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-eas-blue hover:shadow-sm transition-all cursor-pointer" onClick={() => openTool(res)}>
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${res.type === 'Video' ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'}`}>{res.type === 'Video' ? <Play size={18} fill="currentColor" /> : <FileText size={18} />}</div>
                                                    <div>
                                                        <h4 className="font-semibold text-slate-800 group-hover:text-eas-blue transition-colors">{res.title}</h4>
                                                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5"><span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide">{res.type}</span>{res.tags && res.tags.length > 0 && (<span className="text-slate-400">• {res.tags.join(', ')}</span>)}</div>
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                                    <button onClick={(e) => { e.stopPropagation(); onAskAi(`Explain ${res.title} in the context of ${module.title}.`); }} className="p-2 rounded-full hover:bg-blue-50 text-slate-400 hover:text-eas-blue transition-colors" title="Ask Coach"><HelpCircle size={18} /></button>
                                                    <span className="p-2 text-eas-blue font-medium text-sm flex items-center">{res.type === 'Video' ? 'Watch' : 'Open'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default TopicView;